// @services/clients/DocumentAssistantSupplyClient.ts
import { v7 as uuidv7 } from "uuid";
import type { TaskEitherTs } from "@ps-bridge/interop";
import API_URL from "@utils/config";
import type {
    SupplyAcceptedResponse,
    SupplyApiDecodeResult,
    SupplyEmailAttachmentRequest,
    SupplyError,
    SupplyJsonPayload,
    SupplyId,
    SupplyManualUploadRequest,
    SupplyStatusResponse,
} from "@services/clients/DocumentAssistantSupplyApi";
import {
    decodeSupplyAcceptedResponse,
    decodeSupplyEmailAttachmentRequest,
    decodeSupplyManualUploadRequest,
    decodeSupplyRefusal,
    decodeSupplyStatusResponse,
    DocumentAssistantSupplyRoutes,
    encodeSupplyEmailAttachmentRequest,
    encodeSupplyManualUploadRequest,
    makeSupplyId,
    supplyErrorFromRefusal,
    supplyLocalError,
} from "@services/clients/DocumentAssistantSupplyApi";
import { taskEitherChain, taskEitherLeft, taskEitherOf, taskEitherTryCatch } from "@utils/effects/branded";

export interface DocumentAssistantSupplyClient {
    manualUpload(request: SupplyManualUploadRequest): TaskEitherTs<SupplyError, SupplyAcceptedResponse>;
    emailAttachment(request: SupplyEmailAttachmentRequest): TaskEitherTs<SupplyError, SupplyAcceptedResponse>;
    status(supplyId: SupplyId): TaskEitherTs<SupplyError, SupplyStatusResponse>;
}

type SupplyResponseDecoder<T> = (payload: SupplyJsonPayload) => SupplyApiDecodeResult<T>;

type SupplyGetRequest<T> = {
    readonly route: string;
    readonly method: "GET";
    readonly decoder: SupplyResponseDecoder<T>;
};

type SupplyPostRequest<T> = {
    readonly route: string;
    readonly method: "POST";
    readonly body: SupplyJsonPayload;
    readonly decoder: SupplyResponseDecoder<T>;
};

type SupplyFetchRequest<T> = SupplyGetRequest<T> | SupplyPostRequest<T>;

const requestDecodeError = (): SupplyError =>
    supplyLocalError("invalidRequest", "Document supply request is invalid.");

const networkError = (): SupplyError =>
    supplyLocalError("networkError", "The document supply service could not be reached.");

const responseDecodeError = (message = "Unable to decode document supply response."): SupplyError =>
    supplyLocalError("responseDecodeError", message);

const statusError = (status: number): SupplyError => {
    switch (status) {
        case 400:
            return supplyLocalError("invalidRequest", "Document supply request was invalid.");
        case 404:
            return supplyLocalError("notFound", "Document supply status was not found.");
        case 409:
            return supplyLocalError("conflict", "Document supply request conflicts with an existing document.");
        case 503:
            return supplyLocalError("serviceUnavailable", "The document supply service is unavailable.");
        default:
            return supplyLocalError(
                status >= 500 ? "serviceUnavailable" : "invalidRequest",
                "Document supply request failed.",
            );
    }
};

const decodeOrLeft = <T>(
    result: SupplyApiDecodeResult<T>,
    decodeError: SupplyError,
): TaskEitherTs<SupplyError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value);
    }

    return taskEitherLeft(decodeError);
};

const errorFromResponseBody = (status: number, bodyText: string): SupplyError => {
    if (bodyText.trim().length === 0) {
        return statusError(status);
    }

    try {
        const decoded = decodeSupplyRefusal(JSON.parse(bodyText));
        if (decoded.type === "ok") {
            return supplyErrorFromRefusal(decoded.value);
        }
        return responseDecodeError("Unable to decode document supply refusal response.");
    } catch {
        return responseDecodeError("Unable to decode document supply refusal response.");
    }
};

const readResponseJson = async (response: Response): Promise<SupplyJsonPayload> => response.json();
const readResponseText = async (response: Response): Promise<string> => response.text();

const mapFetchResponse = <T>(
    response: Response,
    decoder: SupplyResponseDecoder<T>,
): TaskEitherTs<SupplyError, T> => {
    if (!response.ok) {
        const bodyTask = taskEitherTryCatch(
            () => readResponseText(response),
            () => responseDecodeError("Unable to read document supply refusal response."),
        );

        return taskEitherChain(bodyTask, (bodyText) =>
            taskEitherLeft(errorFromResponseBody(response.status, bodyText)),
        );
    }

    const payloadTask = taskEitherTryCatch(
        () => readResponseJson(response),
        () => responseDecodeError(),
    );

    return taskEitherChain(payloadTask, (payload) =>
        decodeOrLeft(decoder(payload), responseDecodeError()),
    );
};

const fetchSupply = <T>({
    route,
    method,
    decoder,
    ...request
}: SupplyFetchRequest<T>): TaskEitherTs<SupplyError, T> => {
    const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Trace-ID": uuidv7(),
    };
    const requestInit: RequestInit = {
        method,
        credentials: "include",
        headers,
    };

    if ("body" in request) {
        headers["Content-Type"] = "application/json";
        requestInit.body = JSON.stringify(request.body);
    }

    const fetchTask = taskEitherTryCatch(
        () => fetch(`${API_URL}${route}`, requestInit),
        () => networkError(),
    );

    return taskEitherChain(fetchTask, (response) => mapFetchResponse(response, decoder));
};

export class DefaultDocumentAssistantSupplyClient implements DocumentAssistantSupplyClient {
    manualUpload(request: SupplyManualUploadRequest): TaskEitherTs<SupplyError, SupplyAcceptedResponse> {
        const encodedRequest = encodeSupplyManualUploadRequest(request);
        const decodedRequest = decodeSupplyManualUploadRequest(encodedRequest);
        if (decodedRequest.type === "error") {
            return taskEitherLeft(requestDecodeError());
        }

        return fetchSupply({
            route: DocumentAssistantSupplyRoutes.manualUpload,
            method: "POST",
            body: encodeSupplyManualUploadRequest(decodedRequest.value),
            decoder: decodeSupplyAcceptedResponse,
        });
    }

    emailAttachment(request: SupplyEmailAttachmentRequest): TaskEitherTs<SupplyError, SupplyAcceptedResponse> {
        const encodedRequest = encodeSupplyEmailAttachmentRequest(request);
        const decodedRequest = decodeSupplyEmailAttachmentRequest(encodedRequest);
        if (decodedRequest.type === "error") {
            return taskEitherLeft(requestDecodeError());
        }

        return fetchSupply({
            route: DocumentAssistantSupplyRoutes.emailAttachment,
            method: "POST",
            body: encodeSupplyEmailAttachmentRequest(decodedRequest.value),
            decoder: decodeSupplyAcceptedResponse,
        });
    }

    status(supplyId: SupplyId): TaskEitherTs<SupplyError, SupplyStatusResponse> {
        const decodedSupplyId = makeSupplyId(supplyId);
        if (decodedSupplyId.type === "error") {
            return taskEitherLeft(requestDecodeError());
        }

        return fetchSupply({
            route: DocumentAssistantSupplyRoutes.status(decodedSupplyId.value),
            method: "GET",
            decoder: decodeSupplyStatusResponse,
        });
    }
}
